'use client';

import * as React from 'react';
import { useEffect, useRef } from 'react';

import { cn } from '@/lib/utils';

import SkipLink from '@/components/atoms/SkipLink';
import type { SkipLinkProps } from '@/components/atoms/SkipLink/SkipLink';

export interface SectionProps extends React.HTMLAttributes<HTMLDivElement> {
    containerClassName?: string;
    skipLinkProps?: SkipLinkProps;
    'data-test-id'?: string;
}

const Section = React.forwardRef<HTMLDivElement, SectionProps>(
    (
        { skipLinkProps, containerClassName, className, children, ...props },
        ref,
    ) => {
        const sectionRef = useRef<HTMLElement | null>(null);
        const nextSectionRef = useRef<HTMLElement | null>(null);

        useEffect(() => {
            const next = sectionRef.current?.nextElementSibling;

            if (next instanceof HTMLElement) {
                nextSectionRef.current = next;
            }
        }, []);

        return (
            <section
                tabIndex={-1}
                className={cn(
                    'w-full scroll-mt-8 overflow-hidden px-6 py-8 md:scroll-mt-12 md:px-8 md:py-12 xl:py-16',
                    skipLinkProps && 'relative',
                    className,
                )}
                ref={sectionRef}
                {...props}
            >
                {skipLinkProps && (
                    <SkipLink
                        {...skipLinkProps}
                        onClick={() => {
                            nextSectionRef.current?.focus();
                        }}
                    />
                )}

                <div
                    ref={ref}
                    className={cn('container', containerClassName)}
                >
                    {children}
                </div>
            </section>
        );
    },
);

export default Section;
